import { contentMaxWidths, readingContent } from "@/src/theme/layout";
import { Alert } from "@/src/lib/productAlert";
import { supportsMediaEditing } from "@/src/lib/platformCapabilities";
import { InsetModal as Modal } from "@/src/components/ui/InsetModal";
import { Text, TextInput as BrandTextInput } from "@/src/components/ui/BrandText";
import { border, brand, surface, text as uiText } from "@/src/theme/tokens";
import { Ionicons } from "@/src/components/ui/ionicons";
import * as DocumentPicker from "expo-document-picker";
import * as ImagePicker from "expo-image-picker";
import * as WebBrowser from "expo-web-browser";
import { router, useLocalSearchParams } from "expo-router";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import React, { useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  Image,
  Keyboard,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { PostMediaGallery } from "../../src/components/PostMediaGallery";
import { PostAuthorHeader } from "../../src/components/PostAuthorHeader";
import { useBlockedUsers } from "../../src/hooks/useBlockedUsers";
import { useComments } from "../../src/hooks/useComments";
import { useProfile } from "../../src/hooks/useProfile";
import { blockUser } from "../../src/lib/blocks";
import { auth, db } from "../../src/lib/firebase";
import {
  cleanupCurrentPostImages,
  cleanupCurrentPostPdf,
  createPostUploadError,
  formatAttachmentSize,
  MAX_POST_IMAGE_BYTES,
  pendingImagesFromAssets,
  uploadPostImages,
  uploadPostPdf,
  type PendingAttachment,
} from "../../src/lib/postAttachments";
import {
  buildPostImageEdit,
  getPostImageUrls,
} from "../../src/lib/postImages";
import {
  buildPostEditPdfStoragePath,
  buildPostEditPayload,
  postEditHasChanges,
} from "../../src/lib/postEditing";
import {
  firebaseErrorDetails,
  safePostErrorMessage,
  validateCanonicalPostProfile,
} from "../../src/lib/postPublishing";
import { reportComment, reportPost } from "../../src/lib/reports";
import {
  canPublishDocuments,
  getAccountType,
  getVerificationStatus,
} from "../../src/lib/identity";
import type { FirePost } from "../../src/types/models";

const BG = surface.secondary;
const ACCENT = brand.primary;
const CARD = surface.primary;
const BORDER = border.subtle;
const MUTED = uiText.muted;
const MAX_POST_IMAGES = 6;

type Post = FirePost;

function commentRoleLabel(item: { authorAccountType?: string; authorRole?: string }) {
  const accountType = getAccountType({
    accountType: item.authorAccountType,
    role: item.authorRole,
  });
  if (accountType === "teacher") return "Professeur";
  if (accountType === "institution") return "École / Institution";
  if (accountType === "student") return "Étudiant";
  return "Utilisateur";
}

function toMillis(ts: any): number {
  if (!ts) return 0;
  if (typeof ts?.toMillis === "function") return ts.toMillis();
  if (typeof ts?.seconds === "number") return ts.seconds * 1000;
  return 0;
}

function formatWhen(ts: any) {
  const ms = toMillis(ts);
  if (!ms) return "";
  const d = new Date(ms);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mo} ${hh}:${mm}`;
}

function getPostImages(post: Post | null) {
  return getPostImageUrls(post);
}

function getEditableImageUrls(post: Post | null) {
  return getPostImages(post);
}

export default function PostCommentsScreen() {
  const { postId } = useLocalSearchParams<{ postId: string }>();
  const pid = useMemo(() => String(postId || ""), [postId]);

  const user = auth.currentUser;
  const { profile } = useProfile(user);
  const blockedUserIds = useBlockedUsers(user?.uid);

  const [post, setPost] = useState<Post | null>(null);
  const [loadingPost, setLoadingPost] = useState(true);
  const [postLoadError, setPostLoadError] = useState(false);
  const [postRetryKey, setPostRetryKey] = useState(0);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [editingPost, setEditingPost] = useState(false);
  const [editingText, setEditingText] = useState("");
  const [editingImages, setEditingImages] = useState<string[]>([]);
  const [editingNewImages, setEditingNewImages] = useState<PendingAttachment[]>([]);
  const [editingPdfUrl, setEditingPdfUrl] = useState("");
  const [editingPdfName, setEditingPdfName] = useState("");
  const [editingNewPdf, setEditingNewPdf] = useState<PendingAttachment | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const {
    comments,
    loadingComments,
    commentsError,
    retryComments,
  } = useComments(post?.id, blockedUserIds);
  const imageUrls = useMemo(() => getPostImages(post), [post]);
  const isMine = !!user?.uid && post?.authorUid === user.uid;

  const openProfile = React.useCallback((uid?: string | null) => {
    const cleanUid = String(uid ?? "").trim();
    if (!cleanUid) return;
    router.push(`/profile/${cleanUid}`);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadPost() {
      if (!pid) {
        setPost(null);
        setLoadingPost(false);
        return;
      }

      try {
        setLoadingPost(true);
        setPostLoadError(false);

        const snap = await getDoc(doc(db, "posts", pid));

        if (cancelled) return;

        if (!snap.exists()) {
          setPost(null);
          setLoadingPost(false);
          return;
        }

        const data = snap.data() as any;

        setPost({
          id: snap.id,
          authorUid: data.authorUid,
          authorIdentityId: data.authorIdentityId,
          authorSnapshot: data.authorSnapshot,
          authorEmail: data.authorEmail,
          authorUsername: data.authorUsername,
          authorPhotoURL: data.authorPhotoURL,
          authorRole: data.authorRole,
          authorAccountType: data.authorAccountType,
          authorVerificationStatus: data.authorVerificationStatus,
          authorEntitlements: data.authorEntitlements,
          teacherVerified: data.teacherVerified,
          text: data.text ?? "",
          subject: data.subject,
          track: data.track,
          visibility: data.visibility,
          groupId: data.groupId ?? null,
          groupName: data.groupName,
          groupIsPublic: data.groupIsPublic,
          images: Array.isArray(data.images) ? data.images : [],
          imageUrls: Array.isArray(data.imageUrls) ? data.imageUrls.filter(Boolean) : [],
          attachmentType: data.attachmentType,
          attachmentUrl: data.attachmentUrl,
          attachmentStoragePath: data.attachmentStoragePath,
          attachmentName: data.attachmentName,
          moderationStatus: data.moderationStatus,
          likesCount: data.likesCount,
          likedBy: Array.isArray(data.likedBy) ? data.likedBy : [],
          createdAt: data.createdAt,
        });
      } catch {
        if (!cancelled) {
          setPost(null);
          setPostLoadError(true);
        }
      } finally {
        if (!cancelled) {
          setLoadingPost(false);
        }
      }
    }

    loadPost();

    return () => {
      cancelled = true;
    };
  }, [pid, postRetryKey]);

  async function onSend() {
    if (!user || !profile || !pid || sending) return;

    const username = String(profile?.username ?? "").trim().toLowerCase();
    if (!/^[a-z0-9_]{3,16}$/.test(username)) {
      Alert.alert("Erreur", "Définis d'abord un @username valide dans ton profil.");
      return;
    }

    const clean = text.trim();
    if (clean.length < 1) return;

    if (clean.length > 1000) {
      Alert.alert("Erreur", "Commentaire trop long.");
      return;
    }

    try {
      setSending(true);
      const accountType = getAccountType(profile);
      const verificationStatus = getVerificationStatus(profile);

      await addDoc(collection(db, "posts", pid, "comments"), {
        authorUid: user.uid,
        authorUsername: username,
        ...(accountType ? { authorAccountType: accountType } : {}),
        ...(accountType === "student" || accountType === "teacher"
          ? { authorRole: accountType }
          : {}),
        authorVerificationStatus: verificationStatus,
        ...(profile.entitlements ? { authorEntitlements: profile.entitlements } : {}),
        teacherVerified: accountType === "teacher" && verificationStatus === "verified",
        text: clean,
        createdAt: serverTimestamp(),
      });

      setText("");
      Keyboard.dismiss();
    } catch (e: any) {
      if (__DEV__) console.warn("[post] comment send failed", e?.code ?? "unknown");
      Alert.alert("Envoi impossible", "Le commentaire n'a pas pu être envoyé. Réessaie.");
    } finally {
      setSending(false);
    }
  }

  async function onDeleteComment(commentId: string) {
    if (!pid) return;

    try {
      await deleteDoc(doc(db, "posts", pid, "comments", commentId));
    } catch (e: any) {
      if (__DEV__) console.warn("[post] comment delete failed", e?.code ?? "unknown");
      Alert.alert("Suppression impossible", "Le commentaire n'a pas pu être supprimé. Réessaie.");
    }
  }

  function openEditModal() {
    if (!post) return;
    setEditingText(String(post.text ?? ""));
    setEditingImages(getEditableImageUrls(post));
    setEditingNewImages([]);
    setEditingPdfUrl(post.attachmentType === "pdf" ? String(post.attachmentUrl ?? "") : "");
    setEditingPdfName(post.attachmentType === "pdf" ? String(post.attachmentName ?? "") : "");
    setEditingNewPdf(null);
    setShowActions(false);
    setEditingPost(true);
  }

  function closeEditModal() {
    if (savingEdit) return;
    setEditingPost(false);
    setEditingText("");
    setEditingImages([]);
    setEditingNewImages([]);
    setEditingPdfUrl("");
    setEditingPdfName("");
    setEditingNewPdf(null);
  }

  async function pickEditImages() {
    if (!supportsMediaEditing) return;
    try {
      const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (!permission.granted) {
        Alert.alert("Permission", "Autorise l'accès aux photos pour continuer.");
        return;
      }

      const currentCount = editingImages.length + editingNewImages.length;
      const remainingSlots = MAX_POST_IMAGES - currentCount;
      if (remainingSlots <= 0) {
        Alert.alert("Limite atteinte", `Tu peux ajouter jusqu'à ${MAX_POST_IMAGES} photos.`);
        return;
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ["images"],
        allowsMultipleSelection: true,
        selectionLimit: remainingSlots,
        quality: 1,
        exif: false,
      });

      if (result.canceled || !result.assets?.length) return;

      const { attachments, rejectedTooLarge } = pendingImagesFromAssets(
        result.assets,
        remainingSlots
      );

      if (rejectedTooLarge > 0) {
        Alert.alert(
          "Photo trop lourde",
          `Chaque photo doit faire moins de ${formatAttachmentSize(MAX_POST_IMAGE_BYTES)}.`
        );
      }

      if (attachments.length === 0) return;

      setEditingNewImages((prev) =>
        [...prev, ...attachments].slice(0, MAX_POST_IMAGES - editingImages.length)
      );
    } catch (e: any) {
      const error = createPostUploadError(
        "post/image-picker-failed",
        "Impossible d'ajouter cette photo.",
        e
      );
      console.error("upload:image:error", {
        ...firebaseErrorDetails(error),
        stage: "image-picker",
      });
      Alert.alert("Erreur", safePostErrorMessage(error, "Impossible d’ajouter cette photo."));
    }
  }

  async function pickEditPdf() {
    if (!supportsMediaEditing) return;
    if (!canPublishDocuments(profile)) {
      Alert.alert("Indisponible", "Seuls les professeurs et les institutions peuvent joindre un PDF.");
      return;
    }

    const result = await DocumentPicker.getDocumentAsync({
      type: "application/pdf",
      copyToCacheDirectory: true,
      multiple: false,
    });

    if (result.canceled || !result.assets?.length) return;

    const asset = result.assets[0];
    setEditingNewPdf({
      uri: asset.uri,
      type: "pdf",
      name: asset.name ?? `document_${Date.now()}.pdf`,
      mimeType: asset.mimeType ?? "application/pdf",
      size: asset.size,
    });
    setEditingPdfUrl("");
    setEditingPdfName("");
  }

  async function submitEditPost() {
    if (!post || !user || savingEdit) return;

    const cleanText = editingText.trim();
    const hasAttachmentsAfterEdit =
      editingImages.length > 0 ||
      editingNewImages.length > 0 ||
      !!editingPdfUrl ||
      !!editingNewPdf;

    if (!cleanText && !hasAttachmentsAfterEdit) {
      Alert.alert("Erreur", "Ajoute du texte ou garde au moins une pièce jointe.");
      return;
    }

    if (cleanText.length > 3000) {
      Alert.alert("Erreur", "Le post est trop long.");
      return;
    }

    if (!postEditHasChanges({
      post,
      text: cleanText,
      retainedImageUrls: editingImages,
      newImageCount: editingNewImages.length,
      pdfUrl: editingPdfUrl,
      pdfName: editingPdfName,
      hasNewPdf: !!editingNewPdf,
    })) {
      closeEditModal();
      return;
    }

    let uploadedNewImages: Awaited<ReturnType<typeof uploadPostImages>> = [];
    let newPdfStoragePath: string | undefined;

    try {
      setSavingEdit(true);

      validateCanonicalPostProfile(
        user.uid,
        user.email,
        profile as unknown as Record<string, unknown> | null
      );
      const editAuthSession = { token: await user.getIdToken() };

      uploadedNewImages =
        editingNewImages.length > 0
          ? await uploadPostImages({
              uid: user.uid,
              postId: post.id,
              images: editingNewImages,
              authSession: editAuthSession,
            })
          : [];
      const imageEdit = buildPostImageEdit({
        post,
        retainedUrls: editingImages,
        uploadedImages: uploadedNewImages,
      });
      let nextPdf: Awaited<ReturnType<typeof uploadPostPdf>> | null = null;
      if (editingNewPdf) {
        const storagePath = buildPostEditPdfStoragePath(user.uid, post.id);
        newPdfStoragePath = storagePath;
        nextPdf = await uploadPostPdf({
          uid: user.uid,
          attachment: editingNewPdf,
          storagePath,
          authSession: editAuthSession,
        });
      }
      const editPdf = nextPdf
        ? {
            attachmentUrl: nextPdf.attachmentUrl,
            attachmentStoragePath: nextPdf.attachmentStoragePath,
            attachmentName: nextPdf.attachmentName,
          }
        : editingPdfUrl
        ? {
            attachmentUrl: editingPdfUrl,
            attachmentStoragePath: post.attachmentStoragePath,
            attachmentName: editingPdfName,
          }
        : null;
      const updatePayload = buildPostEditPayload({
        text: cleanText,
        imageEdit,
        pdf: editPdf,
      });

      await updateDoc(doc(db, "posts", pid), updatePayload);

      setPost((prev) =>
        prev
          ? {
              ...prev,
              text: cleanText,
              images: imageEdit.images,
              imageUrls: imageEdit.legacyImageUrls,
              attachmentType: editPdf
                ? "pdf"
                : imageEdit.images.length > 0 || imageEdit.legacyImageUrls.length > 0
                ? "image"
                : undefined,
              attachmentUrl: editPdf?.attachmentUrl || undefined,
              attachmentStoragePath: editPdf?.attachmentStoragePath || undefined,
              attachmentName: editPdf?.attachmentName || undefined,
              moderationStatus: "pending",
            }
          : prev
      );

      closeEditModal();
    } catch (e: any) {
      if (uploadedNewImages.length > 0) {
        try {
          await cleanupCurrentPostImages({
            uid: user.uid,
            postId: pid,
            storagePaths: uploadedNewImages.map((image) => image.storagePath),
          });
        } catch (cleanupError) {
          console.error("edit-post:rollback:error", firebaseErrorDetails(cleanupError));
        }
      }
      if (newPdfStoragePath) {
        try {
          await cleanupCurrentPostPdf({
            uid: user.uid,
            postId: post.id,
            storagePath: newPdfStoragePath,
          });
        } catch (cleanupError) {
          console.error("edit-post:rollback:error", {
            ...firebaseErrorDetails(cleanupError),
            stage: "delete-new-pdf",
            postId: post.id,
            storagePath: newPdfStoragePath,
          });
        }
      }
      console.error("edit-post:error", {
        ...firebaseErrorDetails(e),
        stage: uploadedNewImages.length > 0 ? "firestore" : "profile-or-upload",
        postId: pid,
        storagePath: uploadedNewImages.at(-1)?.storagePath ?? null,
      });
      Alert.alert("Erreur", safePostErrorMessage(e, "Impossible de modifier le post."));
    } finally {
      setSavingEdit(false);
    }
  }

  function confirmDeletePost() {
    if (!pid) return;
    setShowActions(false);
    Alert.alert("Supprimer ce post ?", "Cette action est irreversible.", [
      { text: "Annuler", style: "cancel" },
      {
        text: "Supprimer",
        style: "destructive",
        onPress: async () => {
          try {
            await deleteDoc(doc(db, "posts", pid));
            router.back();
          } catch (e: any) {
            if (__DEV__) console.warn("[post] delete failed", e?.code ?? "unknown");
            Alert.alert("Suppression impossible", "La publication n'a pas pu être supprimée. Réessaie.");
          }
        },
      },
    ]);
  }

  async function reportCurrentPost() {
    if (!post || !user) return;

    try {
      await reportPost({
        reporterUid: user.uid,
        postId: post.id,
        reason: "other",
        details: "Signalement depuis le détail du post",
      });
      setShowActions(false);
      Alert.alert("Signalement envoye", "Merci, ce contenu sera examine.");
    } catch (e: any) {
      if (__DEV__) console.warn("[post] report failed", e?.code ?? "unknown");
      Alert.alert("Signalement impossible", "La publication n'a pas pu être signalée. Réessaie.");
    }
  }

  async function blockPostAuthor() {
    if (!post || !user || post.authorUid === user.uid) return;

    try {
      await blockUser({ uid: user.uid, blockedUid: post.authorUid });
      setShowActions(false);
      Alert.alert("Utilisateur bloqué", "Ses contenus seront masqués pour toi.");
      router.back();
    } catch (e: any) {
      if (__DEV__) console.warn("[post] block failed", e?.code ?? "unknown");
      Alert.alert("Action impossible", "Cet utilisateur n'a pas pu être bloqué. Réessaie.");
    }
  }

  function openCommentActions(item: any) {
    if (!user || item.authorUid === user.uid) return;

    Alert.alert("Commentaire", "Choisis une action.", [
      { text: "Annuler", style: "cancel" },
      {
        text: "Signaler",
        onPress: async () => {
          try {
            await reportComment({
              reporterUid: user.uid,
              postId: pid,
              commentId: item.id,
              reason: "other",
              details: "Signalement de commentaire",
            });
            Alert.alert("Signalement envoye", "Merci, ce commentaire sera examine.");
          } catch (e: any) {
            if (__DEV__) console.warn("[post] comment report failed", e?.code ?? "unknown");
            Alert.alert("Signalement impossible", "Le commentaire n'a pas pu être signalé. Réessaie.");
          }
        },
      },
      {
        text: "Bloquer l'auteur",
        style: "destructive",
        onPress: async () => {
          try {
            await blockUser({ uid: user.uid, blockedUid: item.authorUid });
            Alert.alert("Utilisateur bloqué", "Ses commentaires seront masqués pour toi.");
          } catch (e: any) {
            if (__DEV__) console.warn("[post] comment author block failed", e?.code ?? "unknown");
            Alert.alert("Action impossible", "Cet utilisateur n'a pas pu être bloqué. Réessaie.");
          }
        },
      },
    ]);
  }

  function renderPost() {
    if (!post) return null;

    return (
      <View
        style={{
          backgroundColor: CARD,
          borderWidth: 1,
          borderColor: BORDER,
          borderRadius: 18,
          padding: 16,
          marginBottom: 16,
        }}
      >
        <View style={{ flexDirection: "row", alignItems: "flex-start", gap: 8 }}>
          <View style={{ flex: 1, minWidth: 0 }}>
            <PostAuthorHeader
              post={post}
              onAuthorPress={openProfile}
              onGroupPress={(groupId) => router.push(`/group/${groupId}`)}
            />
          </View>
          <Pressable
            onPress={() => setShowActions(true)}
            accessibilityRole="button"
            accessibilityLabel="Ouvrir les actions du post"
            hitSlop={8}
            style={{ width: 44, height: 44, alignItems: "center", justifyContent: "center" }}
          >
            <Ionicons name="ellipsis-vertical" size={18} color={uiText.secondary} />
          </Pressable>
        </View>

        {!!post.text && (
          <Text style={{ color: uiText.primary, fontSize: 16, lineHeight: 24, marginTop: 10 }}>
            {post.text}
          </Text>
        )}

        <PostMediaGallery imageUrls={imageUrls} />

        {post.attachmentType === "pdf" && post.attachmentUrl ? (
          <Pressable
            onPress={() => WebBrowser.openBrowserAsync(post.attachmentUrl!)}
            accessibilityRole="button"
            accessibilityLabel={`Ouvrir la pièce jointe ${post.attachmentName || "PDF"}`}
            style={{
              marginTop: 12,
              borderRadius: 14,
              borderWidth: 1,
              borderColor: BORDER,
              backgroundColor: surface.primary,
              padding: 12,
              flexDirection: "row",
              alignItems: "center",
              gap: 10,
            }}
          >
            <Ionicons name="document-text-outline" size={20} color={uiText.primary} />
            <View style={{ flex: 1 }}>
              <Text style={{ color: uiText.primary, fontWeight: "800" }} numberOfLines={1}>
                {post.attachmentName || "Document PDF"}
              </Text>
              <Text style={{ color: MUTED, marginTop: 2 }}>Appuie pour ouvrir</Text>
            </View>
          </Pressable>
        ) : null}

      </View>
    );
  }

  function renderCommentsHeader() {
    return (
      <View>
        {renderPost()}
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <Text style={{ color: uiText.primary, fontSize: 18, fontWeight: "900" }}>Commentaires</Text>
          {!loadingComments && comments.length > 0 ? (
            <Text style={{ color: MUTED, fontSize: 12 }}>{comments.length}</Text>
          ) : null}
        </View>
        {commentsError ? (
          <View
            style={{
              backgroundColor: surface.danger,
              borderWidth: 1,
              borderColor: border.danger,
              borderRadius: 14,
              padding: 12,
              marginBottom: 12,
              gap: 8,
            }}
          >
            <Text style={{ color: uiText.danger, fontWeight: "800" }}>
              {commentsError.message}
            </Text>
            <Pressable
              onPress={retryComments}
              disabled={loadingComments}
              accessibilityRole="button"
              accessibilityLabel="Réessayer de charger les commentaires"
              style={{
                alignSelf: "flex-start",
                minHeight: 44,
                justifyContent: "center",
                paddingHorizontal: 10,
                opacity: loadingComments ? 0.6 : 1,
              }}
            >
              <Text style={{ color: uiText.brand, fontWeight: "900" }}>Réessayer</Text>
            </Pressable>
          </View>
        ) : loadingComments ? (
          <View accessibilityLiveRegion="polite" style={{ alignItems: "center", gap: 6, marginBottom: 12, paddingVertical: 8 }}>
            <ActivityIndicator color={uiText.brand} />
            <Text style={{ color: MUTED, fontSize: 12 }}>Chargement…</Text>
          </View>
        ) : null}
      </View>
    );
  }

  function renderComment({ item }: any) {
    const mine = user?.uid === item.authorUid;

    return (
      <View
        style={{
          backgroundColor: CARD,
          borderWidth: 1,
          borderColor: BORDER,
          borderRadius: 16,
          padding: 14,
          marginBottom: 10,
        }}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <View style={{ flex: 1 }}>
            <Pressable disabled={!item.authorUid} onPress={() => openProfile(item.authorUid)}>
              <Text style={{ color: uiText.primary, fontWeight: "800" }}>
                @{item.authorUsername || "utilisateur"}
              </Text>
            </Pressable>

            <Text style={{ color: MUTED, fontSize: 12, marginTop: 2, flexShrink: 1 }}>
              {commentRoleLabel(item)}
              {item.createdAt ? ` · ${formatWhen(item.createdAt)}` : ""}
            </Text>
          </View>

          {mine ? (
            <Pressable
              onPress={() => onDeleteComment(item.id)}
              accessibilityRole="button"
              accessibilityLabel="Supprimer le commentaire"
              style={{ width: 44, height: 44, alignItems: "center", justifyContent: "center" }}
            >
              <Ionicons name="trash-outline" size={18} color={uiText.danger} />
            </Pressable>
          ) : (
            <Pressable
              onPress={() => openCommentActions(item)}
              accessibilityRole="button"
              accessibilityLabel="Ouvrir les actions du commentaire"
              style={{ width: 44, height: 44, alignItems: "center", justifyContent: "center" }}
            >
              <Ionicons name="ellipsis-vertical" size={18} color={uiText.secondary} />
            </Pressable>
          )}
        </View>

        <Text style={{ color: uiText.primary, lineHeight: 22, marginTop: 10 }}>
          {item.text}
        </Text>
      </View>
    );
  }

  const loading = loadingPost;

  if (loading) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: BG }}>
        <View style={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
          <ActivityIndicator color={uiText.brand} />
        </View>
      </SafeAreaView>
    );
  }

  if (!post) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: BG }}>
        <View style={{ padding: 16, gap: 16 }}>
          <Pressable
            onPress={() => router.back()}
            accessibilityRole="button"
            accessibilityLabel="Retour"
            hitSlop={8}
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              backgroundColor: CARD,
              borderWidth: 1,
              borderColor: BORDER,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Ionicons name="chevron-back" size={20} color={uiText.primary} />
          </Pressable>

          <View
            style={{
              borderRadius: 18,
              padding: 16,
              backgroundColor: CARD,
              borderWidth: 1,
              borderColor: BORDER,
            }}
          >
            <Text style={{ color: uiText.primary, fontSize: 18, fontWeight: "900" }}>
              {postLoadError ? "Impossible de charger le post" : "Post introuvable"}
            </Text>

            <Text style={{ color: MUTED, marginTop: 8, lineHeight: 20 }}>
              {postLoadError
                ? "Vérifie ta connexion puis réessaie."
                : "Cette publication a peut-être été supprimée."}
            </Text>

            {postLoadError ? (
              <Pressable
                onPress={() => setPostRetryKey((value) => value + 1)}
                accessibilityRole="button"
                accessibilityLabel="Réessayer de charger le post"
                style={{ alignSelf: "flex-start", marginTop: 12, paddingVertical: 8 }}
              >
                <Text style={{ color: uiText.brand, fontWeight: "900" }}>Réessayer</Text>
              </Pressable>
            ) : null}
          </View>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: BG }}>
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        keyboardVerticalOffset={10}
      >

        <View style={{ flex: 1 }}>
          <View
            style={{
              ...readingContent(contentMaxWidths.post),
              flexDirection: "row",
              alignItems: "center",
              gap: 12,
              paddingHorizontal: 16,
              paddingTop: 8,
              paddingBottom: 10,
            }}
          >
            <Pressable
              onPress={() => router.back()}
              accessibilityRole="button"
              accessibilityLabel="Retour"
              hitSlop={8}
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                backgroundColor: CARD,
                borderWidth: 1,
                borderColor: BORDER,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Ionicons name="chevron-back" size={20} color={uiText.primary} />
            </Pressable>

            <Text style={{ color: uiText.primary, fontSize: 18, fontWeight: "900" }}>
              Publication
            </Text>
          </View>

          <FlatList
            data={comments}
            keyExtractor={(item) => item.id}
            renderItem={renderComment}
            keyboardShouldPersistTaps="handled"
            initialNumToRender={10}
            maxToRenderPerBatch={10}
            windowSize={7}
            removeClippedSubviews={Platform.OS === "android"}
            contentContainerStyle={{
              ...readingContent(contentMaxWidths.post),
              paddingHorizontal: 16,
              paddingTop: 6,
              paddingBottom: 120,
            }}
            ListHeaderComponent={renderCommentsHeader}
            ListEmptyComponent={
              commentsError || loadingComments ? null : (
                <Text style={{ color: uiText.muted }}>
                  Aucun commentaire pour le moment
                </Text>
              )
            }
          />

          <Modal
            visible={showActions}
            transparent
            animationType="fade"
            onRequestClose={() => setShowActions(false)}
          >
            <Pressable
              onPress={() => setShowActions(false)}
              style={{
                flex: 1,
                backgroundColor: surface.overlay,
                justifyContent: Platform.OS === "web" ? "center" : "flex-end",
                alignItems: "center",
                padding: Platform.OS === "web" ? 16 : 0,
              }}
            >
              <Pressable
                onPress={() => {}}
                style={{
                  width: "100%",
                  maxWidth: Platform.OS === "web" ? contentMaxWidths.dialog : undefined,
                  borderTopLeftRadius: 22,
                  borderTopRightRadius: 22,
                  backgroundColor: surface.primary,
                  borderTopWidth: 1,
                  borderLeftWidth: 1,
                  borderRightWidth: 1,
                  borderColor: border.subtle,
                  padding: 16,
                  gap: 10,
                }}
              >
                <View
                  style={{
                    alignSelf: "center",
                    width: 44,
                    height: 5,
                    borderRadius: 999,
                    backgroundColor: surface.elevated,
                  }}
                />

                <View
                  style={{
                    borderRadius: 18,
                    backgroundColor: surface.primary,
                    borderWidth: 1,
                    borderColor: border.subtle,
                    padding: 10,
                  }}
                >
                  <Text style={{ color: MUTED }} numberOfLines={2}>
                    {String(post?.text ?? "Choisis une action pour ce post.")}
                  </Text>
                </View>

                {isMine ? (
                  <>
                    <Pressable
                      onPress={openEditModal}
                      style={{
                        borderRadius: 16,
                        paddingHorizontal: 14,
                        paddingVertical: 14,
                        backgroundColor: surface.selected,
                        borderWidth: 1,
                        borderColor: border.brand,
                      }}
                    >
                      <Text style={{ color: uiText.primary, fontWeight: "900" }}>Modifier</Text>

                    </Pressable>

                    <Pressable
                      onPress={confirmDeletePost}
                      style={{
                        borderRadius: 16,
                        paddingHorizontal: 14,
                        paddingVertical: 14,
                        backgroundColor: surface.danger,
                        borderWidth: 1,
                        borderColor: border.danger,
                      }}
                    >
                      <Text style={{ color: uiText.danger, fontWeight: "900" }}>Supprimer</Text>

                    </Pressable>
                  </>
                ) : (
                  <>
                    <Pressable
                      onPress={reportCurrentPost}
                      style={{
                        borderRadius: 16,
                        paddingHorizontal: 14,
                        paddingVertical: 14,
                        backgroundColor: surface.warning,
                        borderWidth: 1,
                        borderColor: brand.accent,
                      }}
                    >
                      <Text style={{ color: brand.accent, fontWeight: "900" }}>Signaler</Text>

                    </Pressable>

                    <Pressable
                      onPress={blockPostAuthor}
                      style={{
                        borderRadius: 16,
                        paddingHorizontal: 14,
                        paddingVertical: 14,
                        backgroundColor: surface.danger,
                        borderWidth: 1,
                        borderColor: border.danger,
                      }}
                    >
                      <Text style={{ color: uiText.danger, fontWeight: "900" }}>Bloquer</Text>

                    </Pressable>
                  </>
                )}

                <Pressable
                  onPress={() => setShowActions(false)}
                  style={{ alignItems: "center", paddingVertical: 10 }}
                >
                  <Text style={{ color: MUTED, fontWeight: "800" }}>Annuler</Text>
                </Pressable>
              </Pressable>
            </Pressable>
          </Modal>

          <Modal
            visible={editingPost}
            transparent
            animationType="fade"
            onRequestClose={closeEditModal}
          >
            <KeyboardAvoidingView
              behavior={Platform.OS === "ios" ? "padding" : "height"}
              style={{
                flex: 1,
                backgroundColor: surface.overlay,
                justifyContent: "center",
                padding: 16,
              }}
            >
<ScrollView keyboardShouldPersistTaps="handled" contentContainerStyle={{ flexGrow: 1, justifyContent: "center" }}>
              <Pressable
                onPress={closeEditModal}
                style={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  bottom: 0,
                  left: 0,
                }}
              />
              <View
                style={{
                  borderRadius: 22,
                  backgroundColor: surface.primary,
                  borderWidth: 1,
                  borderColor: border.subtle,
                  padding: 18,
                  gap: 14,
                }}
              >
                <View>
                  <Text style={{ color: uiText.primary, fontSize: 20, fontWeight: "900" }}>
                    Modifier le post
                  </Text>
                  <Text style={{ color: MUTED, marginTop: 6 }}>
                    {supportsMediaEditing ? "Texte, photos et PDF." : "L’édition Web conserve les médias existants. Pour les remplacer, crée une nouvelle publication."}
                  </Text>
                </View>

                <View
                  style={{
                    borderRadius: 18,
                    borderWidth: 1,
                    borderColor: border.subtle,
                    backgroundColor: surface.primary,
                    padding: 12,
                  }}
                >
                  <BrandTextInput
                    value={editingText}
                    onChangeText={setEditingText}
                  placeholder="Écris ton post…"
                    placeholderTextColor={uiText.muted}
                    multiline
                    maxLength={3000}
                    style={{
                      minHeight: 140,
                      maxHeight: 240,
                      color: uiText.primary,
                      fontSize: 15,
                      lineHeight: 22,
                      textAlignVertical: "top",
                    }}
                  />
                </View>

                <View style={{ gap: 10 }}>
                  <View
                    style={{
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Text style={{ color: uiText.primary, fontWeight: "800" }}>Photos</Text>
                    <Pressable
                      disabled={!supportsMediaEditing}
                      accessibilityState={{ disabled: !supportsMediaEditing }}
                      onPress={pickEditImages}
                      style={{
                        borderRadius: 12,
                        paddingHorizontal: 12,
                        paddingVertical: 8,
                        backgroundColor: surface.selected,
                        borderWidth: 1,
                        borderColor: border.brand,
                      }}
                    >
                      <Text style={{ color: uiText.primary, fontWeight: "800" }}>Ajouter</Text>
                    </Pressable>
                  </View>

                  {editingImages.length + editingNewImages.length > 0 ? (
                    <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                      <View style={{ flexDirection: "row", gap: 10 }}>
                        {editingImages.map((imageUrl, index) => (
                          <View key={`existing_${imageUrl}_${index}`}>
                            <Image
                              source={{ uri: imageUrl }}
                              style={{
                                width: 90,
                                height: 90,
                                borderRadius: 14,
                                backgroundColor: surface.primary,
                              }}
                            />
                            <Pressable
                              onPress={() =>
                                setEditingImages((prev) =>
                                  prev.filter((_, currentIndex) => currentIndex !== index)
                                )
                              }
                              style={{
                                position: "absolute",
                                top: 6,
                                right: 6,
                                backgroundColor: surface.overlay,
                                borderRadius: 999,
                                paddingHorizontal: 8,
                                paddingVertical: 4,
                              }}
                            >
                              <Text style={{ color: uiText.primary, fontSize: 12, fontWeight: "900" }}>
                                Retirer
                              </Text>
                            </Pressable>
                          </View>
                        ))}

                        {editingNewImages.map((image, index) => (
                          <View key={`new_${image.uri}_${index}`}>
                            <Image
                              source={{ uri: image.uri }}
                              style={{
                                width: 90,
                                height: 90,
                                borderRadius: 14,
                                backgroundColor: surface.primary,
                              }}
                            />
                            <Pressable
                              onPress={() =>
                                setEditingNewImages((prev) =>
                                  prev.filter((_, currentIndex) => currentIndex !== index)
                                )
                              }
                              style={{
                                position: "absolute",
                                top: 6,
                                right: 6,
                                backgroundColor: surface.overlay,
                                borderRadius: 999,
                                paddingHorizontal: 8,
                                paddingVertical: 4,
                              }}
                            >
                              <Text style={{ color: uiText.primary, fontSize: 12, fontWeight: "900" }}>
                                Retirer
                              </Text>
                            </Pressable>
                          </View>
                        ))}
                      </View>
                    </ScrollView>
                  ) : (
                    <Text style={{ color: MUTED }}>Aucune photo pour ce post.</Text>
                  )}
                </View>

                {canPublishDocuments(profile) ? (
                  <View style={{ gap: 10 }}>
                    <View
                      style={{
                        flexDirection: "row",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Text style={{ color: uiText.primary, fontWeight: "800" }}>PDF</Text>
                      <View style={{ flexDirection: "row", gap: 8 }}>
                        <Pressable
                          disabled={!supportsMediaEditing}
                      accessibilityState={{ disabled: !supportsMediaEditing }}
                      onPress={pickEditPdf}
                          style={{
                            borderRadius: 12,
                            paddingHorizontal: 12,
                            paddingVertical: 8,
                            backgroundColor: surface.selected,
                            borderWidth: 1,
                            borderColor: border.brand,
                          }}
                        >
                          <Text style={{ color: uiText.primary, fontWeight: "800" }}>
                            {editingPdfUrl || editingNewPdf ? "Remplacer" : "Ajouter"}
                          </Text>
                        </Pressable>

                        {editingPdfUrl || editingNewPdf ? (
                          <Pressable
                            onPress={() => {
                              setEditingPdfUrl("");
                              setEditingPdfName("");
                              setEditingNewPdf(null);
                            }}
                            style={{
                              borderRadius: 12,
                              paddingHorizontal: 12,
                              paddingVertical: 8,
                              backgroundColor: surface.danger,
                              borderWidth: 1,
                              borderColor: border.danger,
                            }}
                          >
                            <Text style={{ color: uiText.danger, fontWeight: "800" }}>Retirer</Text>
                          </Pressable>
                        ) : null}
                      </View>
                    </View>

                    {editingPdfUrl || editingNewPdf ? (
                      <View
                        style={{
                          borderRadius: 14,
                          borderWidth: 1,
                          borderColor: border.subtle,
                          backgroundColor: surface.primary,
                          padding: 12,
                        }}
                      >
                        <Text style={{ color: uiText.primary, fontWeight: "800" }} numberOfLines={1}>
                          {editingNewPdf?.name || editingPdfName || "Document PDF"}
                        </Text>
                      </View>
                    ) : (
                      <Text style={{ color: MUTED }}>Aucun PDF pour ce post.</Text>
                    )}
                  </View>
                ) : null}

                <View
                  style={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Text style={{ color: MUTED }}>{editingText.length}/3000</Text>
                </View>

                <View style={{ flexDirection: "row", gap: 10 }}>
                  <Pressable
                    onPress={closeEditModal}
                    disabled={savingEdit}
                    style={{
                      flex: 1,
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 14,
                      borderWidth: 1,
                      borderColor: border.subtle,
                      backgroundColor: surface.primary,
                      paddingVertical: 14,
                    }}
                  >
                    <Text style={{ color: uiText.primary, fontWeight: "800" }}>Annuler</Text>
                  </Pressable>

                  <Pressable
                    onPress={submitEditPost}
                    disabled={savingEdit}
                    style={{
                      flex: 1,
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 14,
                      backgroundColor: savingEdit ? surface.selected : ACCENT,
                      paddingVertical: 14,
                    }}
                  >
                    <Text style={{ color: uiText.primary, fontWeight: "900" }}>
                      {savingEdit ? "Enregistrement…" : "Enregistrer"}
                    </Text>
                  </Pressable>
                </View>
              </View>
            </ScrollView>
</KeyboardAvoidingView>
          </Modal>

          <View
            style={{
              ...readingContent(contentMaxWidths.post),
              borderTopWidth: 1,
              borderTopColor: BORDER,
              padding: 12,
              backgroundColor: BG,
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "flex-end",
                gap: 10,
              }}
            >
              <BrandTextInput
                value={text}
                onChangeText={setText}
                placeholder="Écrire un commentaire…"
                placeholderTextColor={uiText.muted}
                multiline
                style={{
                  flex: 1,
                  minHeight: 44,
                  maxHeight: 120,
                  color: uiText.primary,
                  backgroundColor: surface.primary,
                  borderWidth: 1,
                  borderColor: BORDER,
                  borderRadius: 14,
                  paddingHorizontal: 14,
                  paddingVertical: 12,
                  textAlignVertical: "top",
                }}
              />

              <Pressable
                onPress={onSend}
                disabled={sending}
                style={{
                  height: 44,
                  paddingHorizontal: 16,
                  borderRadius: 12,
                  backgroundColor: sending ? surface.selected : ACCENT,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Text style={{ color: uiText.primary, fontWeight: "700" }}>
                  {sending ? "…" : "Envoyer"}
                </Text>
              </Pressable>
            </View>
          </View>
        </View>

</KeyboardAvoidingView>
    </SafeAreaView>
  );
}
